import { Component, HostListener, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router,
  RouterModule,
} from '@angular/router';
import { stripDigitGrouping } from '@trading-fe/shared';

@Component({
  imports: [RouterModule],
  selector: 'app-root',
  templateUrl: './app.html',
})
export class App {
  protected title = 'Trading admin';

  private router = inject(Router);

  constructor() {
    this.hideLoaderAfterInitialNavigation();
  }

  /**
   * The #app-loader in index.html covers JS download, app initializers
   * (currencies, precisions) and the initial navigation (guards, lazy chunks).
   * Remove it once the first navigation settles — guard redirects and
   * superseded navigations don't count.
   */
  private hideLoaderAfterInitialNavigation(): void {
    const sub = this.router.events.subscribe((event) => {
      const settled =
        event instanceof NavigationEnd ||
        event instanceof NavigationError ||
        (event instanceof NavigationCancel &&
          event.code !== NavigationCancellationCode.Redirect &&
          event.code !== NavigationCancellationCode.SupersededByNewNavigation);
      if (!settled) return;

      sub.unsubscribe();
      const loader = document.getElementById('app-loader');
      if (!loader) return;
      loader.classList.add('app-loader--hidden');
      setTimeout(() => loader.remove(), 250);
    });
  }

  /**
   * Amounts are displayed with thousands separators, which a spreadsheet will
   * not parse as a number. Copying rewrites the selection so the separators are
   * dropped — one listener covers every table, including selections that span
   * several cells, where a per-cell directive could not help.
   *
   * Only grouped digits are touched; when nothing changes the event is left to
   * the browser so the copy behaves exactly as before.
   */
  @HostListener('document:copy', ['$event'])
  onDocumentCopy(event: ClipboardEvent): void {
    const selected = document.getSelection()?.toString();
    if (!selected) return;

    const cleaned = stripDigitGrouping(selected);
    if (cleaned === selected) return;

    event.clipboardData?.setData('text/plain', cleaned);
    event.preventDefault();
  }
}
